import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Clock, MapPin, User, CheckCircle } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { getAppointments } from '../../services/appointmentApi';
import { useToast } from '../Toast';
import StatusBadge from '../StatusBadge';
import { WidgetGrid } from '../WidgetGrid';
import { WidgetSettingsModal } from '../WidgetSettingsModal';
import { DashboardCustomizeHeader } from '../DashboardCustomizeHeader';
import { useWidgetPreferences } from '../../hooks/useWidgetPreferences';
import { DEMO_EMPLOYEE_APPOINTMENTS } from '../../data/demoDashboardData';

interface TodayAppointment {
  id: string;
  client_name: string;
  property_name: string;
  service_type: string;
  scheduled_date: string;
  status: string;
  duration?: number;
}

const EmployeeDashboard: React.FC<{ isDemoMode?: boolean }> = ({ isDemoMode = false }) => {
  const { t, formatDate, formatTime } = useI18n();
  const { user } = useAuth();
  const { showError } = useToast();
  const dashboardRoleKey = 'employee' as const;
  const {
    preferences,
    updateWidgetVisibility,
    updateWidgetOrder,
    getVisibleWidgets,
    flushSave,
    isLoading: prefsLoading,
    setLayout,
    resetToDefault,
  } = useWidgetPreferences(dashboardRoleKey);
  const [isCustomizeMode, setIsCustomizeMode] = useState(false);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [todayAppointments, setTodayAppointments] = useState<TodayAppointment[]>([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState<TodayAppointment[]>([]);
  const [completedToday, setCompletedToday] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isDemoMode) {
      setTodayAppointments(DEMO_EMPLOYEE_APPOINTMENTS.today);
      setUpcomingAppointments(DEMO_EMPLOYEE_APPOINTMENTS.upcoming);
      setCompletedToday(DEMO_EMPLOYEE_APPOINTMENTS.completedToday);
      setIsLoading(false);
      return;
    }
    if (user?.id) {
      loadEmployeeDashboard();
    }
  }, [user?.id, isDemoMode]);

  const loadEmployeeDashboard = async () => {
    setIsLoading(true);
    try {
      const today = new Date();
      const todayISO = today.toISOString().split('T')[0];
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowISO = tomorrow.toISOString().split('T')[0];

      // Buscar agendamentos do funcionário
      let appointments: any = null;
      try {
        appointments = await getAppointments({
          employee_id: user?.id,
          start_date: todayISO,
        });
      } catch (error: any) {
        // Log detalhado para debug
        if (error.response) {
          console.warn('Erro ao carregar agendamentos do funcionário:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
          });
        } else if (error.request) {
          console.warn('Erro de rede ao carregar agendamentos - servidor não respondeu');
        } else {
          console.warn('Erro ao carregar agendamentos:', error.message);
        }
        // Continuar com arrays vazios
        setTodayAppointments([]);
        setUpcomingAppointments([]);
        setCompletedToday(0);
        setIsLoading(false);
        return;
      }

      if (!appointments) {
        setTodayAppointments([]);
        setUpcomingAppointments([]);
        setCompletedToday(0);
        return;
      }

      const appointmentsArray = Array.isArray(appointments) 
        ? appointments 
        : (appointments?.appointments || appointments?.data || []);

      // Filtrar agendamentos de hoje
      const todayApts = appointmentsArray.filter((apt: any) => {
        const aptDate = new Date(apt.scheduled_date);
        return aptDate.toISOString().split('T')[0] === todayISO;
      }).map((apt: any) => ({
        id: apt.id,
        client_name: apt.client_name || 'Cliente não informado',
        property_name: apt.property_name || 'Propriedade não informada',
        service_type: apt.service_type || 'limpeza',
        scheduled_date: apt.scheduled_date,
        status: apt.status || 'agendado',
        duration: apt.duration || 120,
      }));

      // Filtrar próximos agendamentos (amanhã e depois)
      const upcomingApts = appointmentsArray
        .filter((apt: any) => {
          const aptDate = new Date(apt.scheduled_date);
          return aptDate.toISOString().split('T')[0] >= tomorrowISO;
        })
        .slice(0, 5) // Próximos 5 agendamentos
        .map((apt: any) => ({
          id: apt.id,
          client_name: apt.client_name || 'Cliente não informado',
          property_name: apt.property_name || 'Propriedade não informada',
          service_type: apt.service_type || 'limpeza',
          scheduled_date: apt.scheduled_date,
          status: apt.status || 'agendado',
          duration: apt.duration || 120,
        }));

      // Contar completados hoje
      const completed = todayApts.filter(
        (apt) => apt.status === 'completed' || apt.status === 'concluído' || apt.status === 'concluido'
      ).length;

      // Ordenar por horário
      todayApts.sort((a, b) => {
        const dateA = new Date(a.scheduled_date);
        const dateB = new Date(b.scheduled_date);
        return dateA.getTime() - dateB.getTime();
      });

      setTodayAppointments(todayApts);
      setUpcomingAppointments(upcomingApts);
      setCompletedToday(completed);
    } catch (error: any) {
      // Log detalhado para debug
      console.error('Erro ao processar dados do dashboard:', error);
      // Não mostrar erro ao usuário se for apenas falta de dados
      if (error.message && !error.message.includes('agendamentos')) {
        showError('Erro ao carregar agendamentos do dia');
      }
      // Garantir que os estados estão definidos mesmo em caso de erro
      setTodayAppointments([]);
      setUpcomingAppointments([]);
      setCompletedToday(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getServiceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      limpeza: 'Limpeza',
      deep_cleaning: 'Limpeza Profunda',
      manutencao: 'Manutenção',
      organizacao: 'Organização',
    };
    return labels[type] || type;
  };

  const availableWidgets = useMemo(
    () => [
      { id: 'todayAppointments', title: t('dashboardWidgetTodayAppointments'), description: '' },
      { id: 'upcomingAppointments', title: t('dashboardWidgetUpcoming'), description: '' },
      { id: 'completedToday', title: t('dashboardWidgetCompletedToday'), description: '' },
    ],
    [t]
  );

  const widgets = useMemo(
    () => [
      {
        id: 'completedToday',
        title: t('dashboardWidgetCompletedToday'),
        content: (
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>
            {isLoading ? '...' : completedToday}
          </div>
        ),
      },
      {
        id: 'todayAppointments',
        title: t('dashboardWidgetTodayAppointments'),
        content: isLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>{t('loading')}</div>
        ) : todayAppointments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            <p>{t('dashboardNoAppointmentsToday')}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {todayAppointments.map(appointment => {
              const appointmentDate = new Date(appointment.scheduled_date);
              return (
                <div
                  key={appointment.id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '10px',
                  }}
                >
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <h4 style={{ margin: '0 0 8px 0' }}>{getServiceTypeLabel(appointment.service_type)}</h4>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      {formatTime(appointmentDate)} · {appointment.client_name} · {appointment.property_name}
                    </div>
                  </div>
                  <StatusBadge status={appointment.status} size="medium" showIcon={true} />
                </div>
              );
            })}
          </div>
        ),
      },
      {
        id: 'upcomingAppointments',
        title: t('dashboardWidgetUpcoming'),
        content:
          upcomingAppointments.length === 0 ? (
            <div style={{ color: '#666', fontSize: '14px' }}>{t('dashboardNoUpcoming')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {upcomingAppointments.map(appointment => {
                const appointmentDate = new Date(appointment.scheduled_date);
                return (
                  <div
                    key={appointment.id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{getServiceTypeLabel(appointment.service_type)}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {formatDate(appointmentDate)} {formatTime(appointmentDate)}
                      </div>
                    </div>
                    <StatusBadge status={appointment.status} size="small" showIcon={true} />
                  </div>
                );
              })}
            </div>
          ),
      },
    ],
    [
      isLoading,
      todayAppointments,
      upcomingAppointments,
      completedToday,
      t,
      formatDate,
      formatTime,
    ]
  );

  const handleToggleCustomize = async () => {
    if (isCustomizeMode) {
      try {
        await flushSave();
      } catch {
        showError(t('dashboardSaveError'));
      }
    }
    setIsCustomizeMode(prev => !prev);
  };

  return (
    <div style={{ padding: '30px' }}>
      <div
        style={{
          marginBottom: '30px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>{t('dashboardEmployeeTitle')}</h1>
          <p style={{ color: '#666', margin: 0 }}>{t('dashboardEmployeeSubtitle')}</p>
        </div>
        <DashboardCustomizeHeader
          isCustomizeMode={isCustomizeMode}
          onToggleCustomize={handleToggleCustomize}
          onOpenSettings={() => setShowWidgetSettings(true)}
        />
      </div>

      <WidgetGrid
        widgets={widgets}
        preferences={preferences}
        isLoading={prefsLoading}
        editable={isCustomizeMode}
        columns={3}
        onReorder={updateWidgetOrder}
        onToggleVisibility={updateWidgetVisibility}
        getVisibleWidgets={getVisibleWidgets}
      />

      <WidgetSettingsModal
        isOpen={showWidgetSettings}
        onClose={() => setShowWidgetSettings(false)}
        preferences={preferences}
        availableWidgets={availableWidgets}
        onToggleVisibility={updateWidgetVisibility}
        onSetLayout={setLayout}
        onReset={() => resetToDefault()}
      />
    </div>
  );
};

export default EmployeeDashboard;

